import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import cx from "classnames";

import { addDiscountCode, clearDiscountCode } from "redux/actions/checkout";
import InputField from "shared/components/InputField";
import MentionMeReferee from "./MentionMeReferee";

import { promoOptionsData } from "./PromoCodeOptions";
import styles from "./DiscountCodeField.module.scss";
import { getDiscountError } from "shared/utility";

const DiscountCodeField = ({ className, promoCode, onDiscountRemoved }) => {
  const dispatch = useDispatch();
  const {
    cart,
    checkoutError,
    isClaimed,
    isCheckingOut,
    discountCode: reduxCode,
    isLoggedIn,
  } = useSelector((s) => ({ ...s.checkout, ...s.auth }));
  const [discountCode, setDiscountCode] = useState(reduxCode || "");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const hasDiscount = Boolean(cart.discount);

  useEffect(() => {
    const discountErr = getDiscountError(checkoutError);
    if (discountErr) {
      setError(discountErr);
    }
  }, [checkoutError, dispatch]);

  useEffect(() => {
    // This should only trigger at the start
    if (!reduxCode) return;
    // `hasDiscount` is a dependency, so without this the effect fires again the
    // moment the code lands on the cart and re-applies it. Mounting a second
    // copy of this field used to do the same thing.
    if (hasDiscount) return;
    if ((isLoggedIn && isClaimed) || !isLoggedIn) {
      setError(null);
      setDiscountCode(reduxCode);
      setIsLoading(true);
      dispatch(addDiscountCode(reduxCode))
        .then(
          () => {},
          ({ response }) => {
            setError(response.data.error.message);
            dispatch(clearDiscountCode());
          },
        )
        .finally(() => setIsLoading(false));
    }
  }, [reduxCode, isLoggedIn, dispatch, isClaimed, hasDiscount]);

  useEffect(() => {
    if (!discountCode && cart.discount && cart.discount.code) {
      setDiscountCode(cart.discount.code);
    }
  }, [discountCode, cart.discount, setDiscountCode]);

  useEffect(() => {
    if (promoOptionsData.showBlock && promoCode) {
      setDiscountCode(promoCode);
      dispatch(addDiscountCode(promoCode));
    }
  }, [promoCode, dispatch]);

  const onSubmit = useCallback(() => {
    setIsLoading(true);
    setError(null);
    if (!hasDiscount) {
      dispatch(addDiscountCode(discountCode))
        .then(
          () => {},
          ({ response }) => {
            setError(response.data.error.message);
          },
        )
        .finally(() => setIsLoading(false));
    } else {
      setDiscountCode("");
      onDiscountRemoved();
      dispatch(clearDiscountCode()).finally(() => setIsLoading(false));
    }
  }, [hasDiscount, discountCode, onDiscountRemoved, dispatch]);

  return (
    <>
      <InputField
        className={cx("checkoutDiscountCodeField gkit-my-2", className)}
        label="Discount code"
        name="discountCode"
      >
        <div className="gkit-d-flex gkit-flex-row">
          <div className={cx("gkit-flex-grow", styles.inputContainer)}>
            <input
              className={cx(
                "gkit-input-md gkit-uppercase",
                Boolean(error) && styles.inputError,
                hasDiscount && !error && styles.inputSuccess,
              )}
              disabled={isLoading || hasDiscount || isCheckingOut}
              placeholder="Code"
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
            />
            {hasDiscount && !error && (
              <img
                alt="Successfully Applied Discount"
                src={require("assets/images/iconDiscountTick.svg").default}
              />
            )}
          </div>
          <button
            className={cx(
              "gkit-btn-md gkit-primary gkit-ms-2",
              isLoading && "gkit-loading",
            )}
            disabled={isLoading || isCheckingOut}
            onClick={onSubmit}
          >
            {hasDiscount ? "Remove" : "Apply"}
          </button>
        </div>
        {error && (
          <p className="gkit-p-13 gkit-bold gkit-text-danger gkit-mb-0 gkit-mt-1">{error}</p>
        )}
      </InputField>

      <MentionMeReferee />
    </>
  );
};

export default DiscountCodeField;
